import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { format, formatDistanceToNowStrict, type Locale } from "date-fns"
import { enUS, ja, zhTW } from "date-fns/locale"
import { ConnectionStatusIcon, ProviderLogo } from "@/components"
import { PermissionGuard } from "@/components/Auth"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { useDeleteConnectionApi } from "@/hooks/api"
import { ErrorCode } from "@/constants/errors"
import { Connection, ContextProviderDisplayName, Permission } from "@/types"
import {
  ConnectionActionButton,
  ConnectionDeleteDialog,
} from "@/components/Connection"
import { useToast } from "@/hooks"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

export interface ConnectionListItemProps {
  connection: Connection
  ownerName?: string
  onDelete?: (connection: Connection) => void
}

const ConnectionListItem: React.FC<ConnectionListItemProps> = ({
  connection,
  ownerName,
  onDelete,
}: ConnectionListItemProps) => {
  const { t, i18n } = useTranslation()
  const { alertToast, successToast } = useToast()

  const dateFnsLocale = useMemo(() => {
    const localeMap: Record<string, Locale> = {
      en: enUS,
      "zh-TW": zhTW,
      ja,
    }
    return localeMap[i18n.language] || enUS
  }, [i18n.language])

  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const {
    error: deleteError,
    data: deleteRespData,
    fetchFn: deleteFn,
  } = useDeleteConnectionApi(connection.id)

  useEffect(() => {
    if (!deleteError) return
    console.error("Failed to delete connection", deleteError)

    alertToast({
      title: t("connection.messages.deleteFailed.title"),
      description: getUserFacingApiErrorMessage(
        deleteError,
        t,
        deleteRespData?.message || t("connection.messages.deleteFailed.title")
      ),
    })
  }, [deleteError, deleteRespData, alertToast, t])

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = () => {
    deleteFn({
      callbackFn: (resp) => {
        if (!resp || resp.error_code !== ErrorCode.NONE) return

        successToast({
          title: t("connection.messages.deleteSuccess.title"),
          description: t("connection.messages.deleteSuccess.description", {
            name: connection.name,
          }),
        })
        onDelete?.(connection)
      },
    })
  }

  const providerName =
    ContextProviderDisplayName[connection.provider] || connection.provider

  return (
    <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <ProviderLogo
          provider={connection.provider}
          className="w-6 h-6 flex-shrink-0"
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              {providerName}
            </span>
            <ConnectionStatusIcon
              className="w-3.5 h-3.5 dark:opacity-90"
              status={connection.status}
            />
          </div>
          <div
            className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate"
            title={connection.name}
          >
            {connection.name}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400 sm:justify-end sm:text-right">
        {ownerName && (
          <div className="min-w-[120px]">
            <div className="uppercase text-[11px] tracking-wide text-slate-400 dark:text-slate-500">
              {t("connection.card.columns.owner")}
            </div>
            <div className="text-slate-600 dark:text-slate-300 truncate">
              {ownerName}
            </div>
          </div>
        )}
        <div className="min-w-[160px]">
          <div className="uppercase text-[11px] tracking-wide text-slate-400 dark:text-slate-500">
            {t("connection.card.columns.lastConnectedAt")}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            {connection.lastConnectedAt ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="whitespace-nowrap">
                      {formatDistanceToNowStrict(
                        new Date(connection.lastConnectedAt),
                        {
                          addSuffix: true,
                          locale: dateFnsLocale,
                        }
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs font-medium">
                      {format(
                        connection.lastConnectedAt,
                        "yyyy-MM-dd HH:mm:ss"
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-slate-400 dark:text-slate-500">--</span>
            )}
          </div>
        </div>
      </div>
      <PermissionGuard permission={Permission.OrgUpdateConnection} hide>
        <ConnectionActionButton className="ml-auto" onDelete={handleDelete} />
      </PermissionGuard>

      <ConnectionDeleteDialog
        connection={connection}
        show={showDeleteDialog}
        setShow={setShowDeleteDialog}
        onDelete={handleDeleteConfirm}
      />
    </div>
  )
}

export default ConnectionListItem
